import { useRef } from 'react';
import { useApp } from './AppContext';

// What each AI action is called in the credit history, and its colour in "Đã dùng vào".
export const AI_ACTIONS = {
  meal_analysis: { label: 'Chụp món ăn', color: '#17804f' },
  meal_reanalysis: { label: 'Phân tích lại món', color: '#17804f' },
  workout_analysis: { label: 'Ghi tập luyện', color: '#c2410c' },
  menu: { label: 'Gợi ý thực đơn', color: '#3ba6ab' },
  menu_swap: { label: 'Đổi món', color: '#3ba6ab' },
  goal_suggestion: { label: 'Gợi ý mục tiêu', color: '#8e72d8' },
  takeout_suggestion: { label: 'Gợi ý đặt ngoài', color: '#e67b4b' },
  ad_reward: { label: 'Xem quảng cáo', color: '#ecb441' },
  admin_grant: { label: 'Đội ngũ tặng lượt', color: '#ecb441' },
};
// "Đã dùng vào" groups re-analysis with photos and swaps with menus, like the design.
export const USAGE_GROUPS = [
  { label: 'Chụp món ăn', color: '#17804f', actions: ['meal_analysis', 'meal_reanalysis'] },
  { label: 'Ghi tập luyện', color: '#c2410c', actions: ['workout_analysis'] },
  { label: 'Gợi ý thực đơn', color: '#3ba6ab', actions: ['menu', 'menu_swap'] },
  { label: 'Gợi ý mục tiêu', color: '#8e72d8', actions: ['goal_suggestion'] },
  { label: 'Gợi ý đặt ngoài', color: '#e67b4b', actions: ['takeout_suggestion'] },
];

const newKey = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

// Plays a rewarded ad in the app and waits for its credit. AdMob tells the server (signed callback), which adds
// the credit a moment later, so this polls the membership for a few seconds. Resolves to the new membership
// when the credit arrived; throws when no ad could be shown or the credit didn't come.
export async function watchAdForCredit({ api, native, setMembership, membership }) {
  if (!(await native.showRewardedAd())) throw new Error('Hiện chưa có quảng cáo, thử lại sau');
  const before = membership?.credits?.earned ?? 0;
  for (let i = 0; i < 8; i += 1) {
    const next = await api.get('/membership');
    setMembership(next);
    if (next.credits.earned > before) return next;
    await new Promise((resolve) => { setTimeout(resolve, 1000); });
  }
  throw new Error('Chưa nhận được lượt từ quảng cáo, thử lại sau ít phút');
}

// Free accounts once rewarded ads are on: every AI request needs an ad first.
export const adPerRequest = (membership) => !!membership?.ads?.enabled;

// For one AI form: the "Dùng 1 lượt AI · hôm nay còn N lượt" line, a check before sending (out of credits
// opens the out-of-credits sheet instead of a request the server would refuse), and an idempotency key
// that stays the same until a request succeeds. `blocked(retry)`: when the sheet gets a credit from an ad,
// it calls `retry`, which goes through without checking again (this render's credits are still the old ones).
export function useAiCredits(cost = 1, manual = null) {
  const { membership, openPaywall } = useApp();
  const key = useRef(newKey());
  const granted = useRef(false);
  const credits = membership?.credits;
  const unlimited = membership && credits.left == null;
  // Pro also has a monthly allowance; whichever runs out first counts.
  const left = credits ? Math.min(credits.left ?? Infinity, membership.month?.left ?? Infinity) : undefined;
  const ads = adPerRequest(membership);
  let note = null;
  if (membership) {
    if (unlimited) note = `Dùng ${cost} lượt AI · không giới hạn`;
    else if (ads && credits.left < cost) note = `Xem 1 quảng cáo ngắn để dùng AI · hôm nay còn ${membership.ads.left} lần`;
    else note = `Dùng ${cost} lượt AI · hôm nay còn ${credits.left} lượt${membership.month ? ` · tháng còn ${membership.month.left}` : ''}`;
  }
  return {
    left,
    note,
    blocked: (retry) => {
      if (granted.current) {
        granted.current = false;
        return false;
      }
      if (!membership || unlimited || left >= cost) return false;
      const monthOut = membership.month && membership.month.left < cost;
      openPaywall('credits', {
        manual,
        message: monthOut ? `Bạn đã dùng hết ${membership.month.limit} lượt AI của tháng này. Lượt mới có từ ngày ${membership.month.resets_on.slice(8, 10).replace(/^0/, '')}/${Number(membership.month.resets_on.slice(5, 7))}.` : undefined,
        retry: retry && (() => { granted.current = true; retry(); }),
      });
      return true;
    },
    key: () => key.current,
    used: () => { key.current = newKey(); },
  };
}
